import { useState } from 'react';
import kit from './kit.module.css';
import styles from './AgentScopeDemoAgentic.module.css';

interface Capability {
  id: string;
  label: string;
  lastUsed: string;
  defaultEnabled: boolean;
}

interface Section {
  id: string;
  label: string;
  risk: string;
  riskChip: string;
  capabilities: Capability[];
}

const SECTIONS: Section[] = [
  {
    id: 'reading',
    label: 'Reading access',
    risk: 'Low risk',
    riskChip: kit.chipGood,
    capabilities: [
      { id: 'read-profiles', label: 'Read jobseeker profiles', lastUsed: 'Last used 2 min ago', defaultEnabled: true },
      { id: 'read-employers', label: 'Read employer contact list', lastUsed: 'Last used 1 hour ago', defaultEnabled: true },
    ],
  },
  {
    id: 'writing',
    label: 'Writing access',
    risk: 'Medium risk',
    riskChip: kit.chipWarn,
    capabilities: [
      { id: 'generate-cvs', label: 'Generate CV drafts', lastUsed: 'Last used 1 hour ago', defaultEnabled: true },
      { id: 'save-drafts', label: 'Save drafts to account', lastUsed: 'Last used 1 hour ago', defaultEnabled: true },
      { id: 'edit-cvs', label: 'Edit existing CVs directly', lastUsed: 'Never used', defaultEnabled: false },
    ],
  },
  {
    id: 'sending',
    label: 'Sending & external access',
    risk: 'High risk',
    riskChip: kit.chipBad,
    capabilities: [
      { id: 'send-emails', label: 'Send emails to employers', lastUsed: 'Last used yesterday', defaultEnabled: true },
      { id: 'external-jobs', label: 'Access external job boards', lastUsed: 'Never used', defaultEnabled: false },
      { id: 'share-data', label: 'Share data with third parties', lastUsed: 'Never used', defaultEnabled: false },
    ],
  },
];

const ALL_CAPS = SECTIONS.flatMap((s) => s.capabilities);

function Toggle({ enabled, onChange, label }: { enabled: boolean; onChange: () => void; label: string }) {
  return (
    <button
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      className={`${styles.toggle} ${enabled ? styles.toggleOn : ''}`}
      onClick={onChange}
    >
      <span className={styles.thumb} />
    </button>
  );
}

export function AgentScopeDemoAgentic() {
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(ALL_CAPS.map((c) => [c.id, c.defaultEnabled]))
  );
  const [saved, setSaved] = useState(false);

  function toggle(id: string) {
    setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
    setSaved(false);
  }

  return (
    <div className={kit.root}>
      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✦</span>
        <div>
          <p className={styles.agentName}>CV Assistant</p>
          <span className={kit.label}>Agent settings · Capabilities</span>
        </div>
      </div>

      {SECTIONS.map((section) => {
        const onCount = section.capabilities.filter((c) => enabled[c.id]).length;
        return (
          <section key={section.id} className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>
                {section.label} · {onCount}/{section.capabilities.length} on
              </span>
              <span className={`${kit.chip} ${section.riskChip} ${styles.risk}`}>{section.risk}</span>
            </div>
            <div className={styles.list}>
              {section.capabilities.map((cap) => {
                const isOn = enabled[cap.id];
                return (
                  <div key={cap.id} className={styles.cap}>
                    <div className={styles.capText}>
                      <span className={isOn ? styles.capLabel : `${styles.capLabel} ${styles.capOff}`}>{cap.label}</span>
                      <span className={isOn ? styles.meta : `${styles.meta} ${styles.metaAsk}`}>
                        {isOn ? cap.lastUsed : 'Agent will ask before doing this'}
                      </span>
                    </div>
                    <Toggle enabled={isOn} onChange={() => toggle(cap.id)} label={cap.label} />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className={kit.row}>
        <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setSaved(true)} disabled={saved}>
          Save changes
        </button>
        {saved && <span className={`${kit.chip} ${kit.chipGood}`}>✓ Saved</span>}
        <p className={kit.feedback}>Changes take effect immediately.</p>
      </div>
    </div>
  );
}
